/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState, useRef } from 'react';

import { getPlanStatus, isLenderStepPending } from '../EmiPlans/helper';
import EsignSuccess from './EsignSuccess';
import SuccessScreen from '@/shared/Components/SuccessScreen';
import EmailVerificationPopUp from '@/views/EMandateSuccess/EmailVerificationPopUp';
import { useGlobalStore } from '@/store';
import { getESignUrl } from '@/api/kyc';
import OpenPopupLoader from '@/shared/Components/OpenPopupLoader';
import { PopupFailureProps } from '@/shared/types';
import { PaymentCancelWrapper } from '@/shared/Components/PaymentCancelWrapper';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';
import { EmiPlans } from '@/api/types';

type componentState = {
  showSuccess: boolean;
  showEsignSuccess: boolean;
};

const EMandateSuccess: React.FC = () => {
  const { user, setActiveView, selectedTenure, lenderDetails, activeView, userAccessToken } =
    useGlobalStore();
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const eSignUrlRef = useRef<string | null>(null);
  const [openESignPopup, setOpenESignPopup] = useState(false);
  const [didESignFail, setDidESignFail] = useState(false);
  const [eSignError, setEsignError] = useState('');

  const [state, setState] = useState<componentState>({
    showSuccess: true,
    showEsignSuccess: false,
  });

  const isEsignPending = (): boolean => {
    const lenderStatus = getPlanStatus(lenderDetails, selectedTenure as EmiPlans);
    return Boolean(lenderStatus && isLenderStepPending(lenderStatus.esign));
  };

  const handleNext = async (): Promise<void> => {
    // If esign is not needed (na) or completed redirect to payments flow
    if (!isEsignPending()) {
      setActiveView('PAYMENTS');
      return;
    }

    if (user.email) {
      await getESignUrl()
        .then((eSignResponse) => {
          if (eSignResponse.success && eSignResponse.url) {
            eSignUrlRef.current = eSignResponse.url;
            // Track Esign loader render
            trackHelper({
              actionName: ANALYTICS_ACTIONS.RENDER,
              objectName: EVENT_NAMES.ESIGN_POPUP_RENDER,
              screen: activeView,
              properties: {
                contact: user.contact,
                user_type: getUserLoggedInStatus(userAccessToken, user.contact),
                fc_customer_id: user.id,
                selected_plan: selectedTenure,
                user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
              },
            });
            setOpenESignPopup(true);
            setState({ ...state, showSuccess: false });
          }
        })
        .catch(() => {
          setDidESignFail(true);
          // Track Esign Failure
          trackHelper({
            actionName: ANALYTICS_ACTIONS.RENDER,
            objectName: EVENT_NAMES.ESIGN_POPUP_FAILURE,
            screen: activeView,
            properties: {
              contact: user.contact,
              user_type: getUserLoggedInStatus(userAccessToken, user.contact),
              fc_customer_id: user.id,
              selected_plan: selectedTenure,
              user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
              error_message: 'Esign url fetch failed',
            },
          });
        });
      return;
    }
    setState({ ...state, showSuccess: false });
    setShowEmailVerification(true);
    // Track Verify Email click
    trackHelper({
      objectName: EVENT_NAMES.VERIFY_EMAIL,
      actionName: ANALYTICS_ACTIONS.CLICK,
      screen: activeView,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        fc_customer_id: user.id,
        selectedPlan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
      },
    });
  };

  const handleEsignFailure = (e: PopupFailureProps): void => {
    setDidESignFail(true);
    setOpenESignPopup(false);
    if (e.message) {
      setEsignError(e.message);
    }
    // Track Esign Failure
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.ESIGN_POPUP_FAILURE,
      screen: activeView,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        fc_customer_id: user.id,
        selected_plan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
        error_message: e.message ?? '',
      },
    });
  };

  const handleEsignSuccess = (data: Record<string, any>): void => {
    if (data.razorpay_payment_id) {
      setOpenESignPopup(false);
      setState({
        ...state,
        showSuccess: false,
        showEsignSuccess: true,
      });
      setTimeout(() => {
        setActiveView('PAYMENTS');
      }, 2500);
    } else {
      setDidESignFail(true);
      setOpenESignPopup(false);
    }
  };

  const getUserStatus = (): string => {
    if (!isEsignPending()) {
      return 'Continue';
    }
    return user.email ? 'Continue to E-sign' : 'Verify Email';
  };

  const { showEsignSuccess } = state;

  return (
    <>
      <div className="d:h-full d:flex d:justify-center d:items-center">
        <SuccessScreen
          title="e-Mandate successful!  🎉"
          topDescription="Let's complete email verification for E-Sign"
          ctaText={getUserStatus()}
          onCtaClick={handleNext}
          isFullScreen={true}
        />
      </div>
      {showEsignSuccess ? (
        <EsignSuccess
          isOpen={showEsignSuccess}
          onDismiss={(): void => {
            setState({
              ...state,
              showEsignSuccess: false,
            });
          }}
        />
      ) : null}
      {showEmailVerification ? <EmailVerificationPopUp /> : null}
      {openESignPopup ? (
        <OpenPopupLoader
          serviceType="esign"
          onSuccess={handleEsignSuccess}
          onFail={handleEsignFailure}
          url={eSignUrlRef.current as string}
          serviceName="CashE"
          progressText="e-Sign is in progress on"
        />
      ) : null}
      {didESignFail ? (
        <PaymentCancelWrapper
          isOpen={didESignFail}
          onDismiss={(): null => null}
          ctaText="Change payment method"
          title="E-Sign Failed"
          isCloseable={false}
          description={eSignError || 'E-Sign failed. Try with a different payment method'}
        />
      ) : null}
    </>
  );
};

export default EMandateSuccess;
